import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import {
  Users,
  UserPlus,
  Phone,
  Clock,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Send,
  Trash2,
  AlertCircle,
  Loader2,
  Search,
  Filter,
  MoreVertical,
  Copy,
  Info
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status badge component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    PENDING: {
      label: 'En attente',
      className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      icon: Clock
    },
    ACCEPTED: {
      label: 'Accepté',
      className: 'bg-green-100 text-green-800 border-green-200',
      icon: CheckCircle2
    },
    DECLINED: {
      label: 'Refusé',
      className: 'bg-red-100 text-red-800 border-red-200',
      icon: XCircle
    },
    INVITE_STATUS_UNSPECIFIED: {
      label: 'Non spécifié',
      className: 'bg-gray-100 text-gray-800 border-gray-200',
      icon: AlertCircle
    }
  };

  const config = statusConfig[status] || statusConfig.INVITE_STATUS_UNSPECIFIED;
  const Icon = config.icon;

  return (
    <Badge variant="outline" className={`${config.className} flex items-center gap-1`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
};

export default function RCSTesters({ embedded = false }) {
  const { t } = useTranslation();
  const [testers, setTesters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedTester, setSelectedTester] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  // Form state
  const [newTester, setNewTester] = useState({
    phoneNumber: '',
    agentId: 'default-agent'
  });

  // Stats
  const stats = {
    total: testers.length,
    pending: testers.filter(t => t.inviteStatus === 'PENDING').length,
    accepted: testers.filter(t => t.inviteStatus === 'ACCEPTED').length,
    declined: testers.filter(t => t.inviteStatus === 'DECLINED').length
  };

  const fetchTesters = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/testers?pageSize=100`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Erreur lors du chargement');
      
      const data = await response.json();
      setTesters(data.testers || []);
    } catch (error) {
      console.error('Error fetching testers:', error);
      toast.error('Impossible de charger les testeurs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTesters();
  }, []);

  const handleAddTester = async () => {
    // Validate phone number
    const phoneRegex = /^\+[1-9]\d{6,14}$/;
    const cleanedPhone = newTester.phoneNumber.replace(/\s/g, '');
    
    if (!phoneRegex.test(cleanedPhone)) {
      toast.error('Format de numéro invalide. Utilisez le format E.164 (+33612345678)');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/testers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          phoneNumber: cleanedPhone,
          agentId: newTester.agentId
        })
      });
      
      if (response.status === 409) {
        toast.error('Ce numéro est déjà enregistré comme testeur');
        return;
      }
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de ajout');
      }
      
      toast.success('Invitation envoyée avec succès');
      setIsAddDialogOpen(false);
      setNewTester({ phoneNumber: '', agentId: 'default-agent' });
      fetchTesters();
    } catch (error) {
      console.error('Error adding tester:', error);
      toast.error(error.message || 'Impossible d\'ajouter le testeur');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendInvite = async (tester) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/testers/${encodeURIComponent(tester.phoneNumber)}/resend`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Erreur lors du renvoi');
      
      toast.success('Invitation renvoyée');
    } catch (error) {
      console.error('Error resending invite:', error);
      toast.error('Impossible de renvoyer invitation');
    }
  };

  const handleDeleteTester = async () => {
    if (!selectedTester) return;
    
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/testers/${encodeURIComponent(selectedTester.phoneNumber)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Erreur lors de la suppression');
      
      toast.success('Testeur supprimé');
      setIsDeleteDialogOpen(false);
      setSelectedTester(null);
      fetchTesters();
    } catch (error) {
      console.error('Error deleting tester:', error);
      toast.error('Impossible de supprimer le testeur');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSyncWithGoogle = async () => {
    try {
      setSyncing(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/testers/sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Erreur lors de la synchronisation');
      
      const data = await response.json();
      toast.success(`Synchronisation terminée: ${data.updated || 0} testeurs mis à jour`);
      fetchTesters();
    } catch (error) {
      console.error('Error syncing:', error);
      toast.error('Impossible de synchroniser avec Google');
    } finally {
      setSyncing(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié dans le presse-papier');
  };

  // Filter testers
  const filteredTesters = testers.filter(tester => {
    const matchesSearch = tester.phoneNumber.includes(searchQuery) ||
                         tester.agentId?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || tester.inviteStatus === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const content = (
    <div className="space-y-6 p-6" data-testid="rcs-testers-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="h-7 w-7 text-indigo-600" />
            Testeurs RCS
          </h1>
          <p className="text-slate-500 mt-1">
            Gérez les testeurs pour votre agent RCS Business Messaging
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleSyncWithGoogle}
            disabled={syncing}
            data-testid="sync-google-btn"
          >
            {syncing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Synchroniser
          </Button>
          <Button
            onClick={() => setIsAddDialogOpen(true)}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="add-tester-btn"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Inviter un testeur
          </Button>
        </div>
      </div>

      {/* Migration Alert */}
      <Alert className="border-amber-200 bg-amber-50">
        <AlertCircle className="h-4 w-4 text-amber-600" />
        <AlertTitle className="text-amber-800">Migration Google RCS obligatoire</AlertTitle>
        <AlertDescription className="text-amber-700">
          Cette interface utilise la nouvelle API Google <code className="bg-amber-100 px-1 rounded">testers</code> 
          {' '}qui remplace l'ancienne API <code className="bg-amber-100 px-1 rounded">phones.testers</code>.
          Migration obligatoire avant le 31 janvier 2026.
        </AlertDescription>
      </Alert>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-slate-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total testeurs</p>
                <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              </div>
              <div className="rounded-lg bg-indigo-50 p-3">
                <Users className="h-5 w-5 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">En attente</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
              </div>
              <div className="rounded-lg bg-yellow-50 p-3">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Acceptés</p>
                <p className="text-2xl font-bold text-green-600">{stats.accepted}</p>
              </div>
              <div className="rounded-lg bg-green-50 p-3">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Refusés</p>
                <p className="text-2xl font-bold text-red-600">{stats.declined}</p>
              </div>
              <div className="rounded-lg bg-red-50 p-3">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Testers List */}
      <Card className="border-slate-100">
        <CardHeader className="border-b border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Liste des testeurs</CardTitle>
              <CardDescription>
                Numéros de téléphone autorisés à recevoir des messages de test
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Rechercher..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 w-64"
                  data-testid="search-testers"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="filter-status">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="PENDING">En attente</SelectItem>
                  <SelectItem value="ACCEPTED">Acceptés</SelectItem>
                  <SelectItem value="DECLINED">Refusés</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            </div>
          ) : filteredTesters.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Users className="h-12 w-12 text-slate-300 mb-4" />
              <p className="text-slate-500 font-medium">Aucun testeur</p>
              <p className="text-sm text-slate-400 mt-1">
                {searchQuery || statusFilter !== 'all' 
                  ? 'Aucun résultat pour ces critères'
                  : 'Invitez votre premier testeur pour commencer'}
              </p>
              {!searchQuery && statusFilter === 'all' && (
                <Button 
                  className="mt-4"
                  onClick={() => setIsAddDialogOpen(true)}
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Inviter un testeur
                </Button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredTesters.map((tester) => (
                <div
                  key={`${tester.phoneNumber}-${tester.agentId}`}
                  className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                  data-testid={`tester-${tester.phoneNumber}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="rounded-full bg-indigo-100 p-2">
                      <Phone className="h-5 w-5 text-indigo-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-900">{tester.phoneNumber}</p>
                        <button
                          onClick={() => copyToClipboard(tester.phoneNumber)}
                          className="text-slate-400 hover:text-slate-600"
                        >
                          <Copy className="h-3 w-3" />
                        </button>
                      </div>
                      <p className="text-sm text-slate-500">
                        Agent: {tester.agentId}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right text-sm">
                      <p className="text-slate-500">Invité le</p>
                      <p className="text-slate-700">{formatDate(tester.invitedAt)}</p>
                    </div>
                    
                    <StatusBadge status={tester.inviteStatus} />
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {tester.inviteStatus === 'PENDING' && (
                          <DropdownMenuItem onClick={() => handleResendInvite(tester)}>
                            <Send className="h-4 w-4 mr-2" />
                            Renvoyer invitation
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => copyToClipboard(tester.phoneNumber)}>
                          <Copy className="h-4 w-4 mr-2" />
                          Copier le numéro
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedTester(tester);
                            setIsDeleteDialogOpen(true);
                          }}
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Supprimer
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border-slate-100 bg-slate-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-slate-500 mt-0.5" />
            <div className="text-sm text-slate-600">
              <p className="font-medium text-slate-700 mb-1">Comment fonctionnent les testeurs RCS ?</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Invitez un numéro de téléphone en utilisant le format E.164 (+33612345678)</li>
                <li>Le destinataire reçoit un message RCS lui demandant de devenir testeur</li>
                <li>Une fois accepté, vous pouvez envoyer des messages de test à ce numéro</li>
                <li>Les testeurs permettent de tester votre agent avant le lancement en production</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Tester Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-indigo-600" />
              Inviter un testeur
            </DialogTitle>
            <DialogDescription>
              Envoyez une invitation RCS à un numéro de téléphone pour le tester.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="phoneNumber">Numéro de téléphone</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="phoneNumber"
                  placeholder="+33612345678"
                  value={newTester.phoneNumber}
                  onChange={(e) => setNewTester({ ...newTester, phoneNumber: e.target.value })}
                  className="pl-10"
                  data-testid="input-phone-number"
                />
              </div>
              <p className="text-xs text-slate-500">
                Format E.164 requis (ex: +33612345678)
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="agentId">Agent RCS</Label>
              <Select 
                value={newTester.agentId} 
                onValueChange={(value) => setNewTester({ ...newTester, agentId: value })}
              >
                <SelectTrigger data-testid="select-agent">
                  <SelectValue placeholder="Sélectionner un agent" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default-agent">Agent par défaut</SelectItem>
                  <SelectItem value="test-agent-001">Agent de test</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleAddTester}
              disabled={submitting || !newTester.phoneNumber}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="submit-add-tester"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Envoyer invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Supprimer le testeur
            </DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer ce testeur ? Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          
          {selectedTester && (
            <div className="py-4">
              <div className="rounded-lg border border-red-100 bg-red-50 p-4">
                <div className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-red-600" />
                  <div>
                    <p className="font-medium text-red-900">{selectedTester.phoneNumber}</p>
                    <p className="text-sm text-red-700">Agent: {selectedTester.agentId}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Annuler
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteTester}
              disabled={submitting}
              data-testid="confirm-delete-tester"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  if (embedded) {
    return content;
  }

  return <Layout>{content}</Layout>;
}
